/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  MouseEvent,
  ReactNode,
  TouchEvent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { getReducerInitState, reducer } from './CarouselReducer';
import {
  BreakpointList,
  CarouselAutoplay,
  CarouselControls,
  CarouselEvents,
  CarouselTransition,
  ComponentModularClasses,
  SettingsBreakpoint,
  SlideProps,
  slideDirection,
  renderComponentsParams,
  RequiredCarouselBaseProps,
  CarouselDisplay,
  CarouselSlideList,
} from './CarouselInterfaces';
import useElementOnScreen from './hooks/useElementOnScreen';
import {
  useBemBlockClassNames,
  useBemElementClassNames,
  useBemModifierClassNames,
} from './hooks/useBemClassNames';

import defaultSettings from './defaultSettings';
import StyledCarousel, { StyledCarouselSlideElement } from './styledCarousel';

import Arrows from './controls/arrows';
import Dots from './controls/dots';

interface CarouselBaseProps {
  /**
   * Array for slides data
   */
  data?: SlideProps[];
  /**
   * Method to display the slide from a data
   */
  renderComponentMethod?: (params: renderComponentsParams) => ReactNode;
  /**
   * Define if the Carousel is infinite
   */
  isInfinite?: boolean;
  /**
   * Carousel display
   */
  display?: CarouselDisplay;
  /**
   * Carousel classes
   */
  classes?: ComponentModularClasses;
  /**
   * Transition settings
   */
  transition?: CarouselTransition;
  /**
   * Autoplay settings
   */
  autoplay?: CarouselAutoplay;
  /**
   * All slideList settings
   */
  slideList?: CarouselSlideList;
  /**
   * All carousel controls settings
   */
  controls?: CarouselControls;
  /**
   * All carousel events
   */
  events?: CarouselEvents;
  /**
   * Custom current index set by props
   */
  customCurrentIndex?: number;
}

export interface CarouselProps extends CarouselBaseProps {
  breakpointList?: BreakpointList;
}

const Carousel = ({ customCurrentIndex = 0, ...props }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [mergedProps, setMergedProps] =
    useState<RequiredCarouselBaseProps>(defaultSettings);
  const [numberOfItemBySlide, setNumberOfItemBySlide] = useState(1);
  const [numberOfSlide, setNumberOfSlide] = useState(
    Math.ceil(mergedProps.data.length / numberOfItemBySlide),
  );
  const [carouselState, carouselDispatch] = useReducer(
    reducer,
    getReducerInitState(0, numberOfSlide - 1),
  );
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeStart, setSwipeStart] = useState(0);
  const [swipeEnd, setSwipeEnd] = useState(0);
  const [isChangingSlide, setIsChangingSlide] = useState<boolean>(false);
  const [autoplayStatus, setAutoplayStatus] = useState(
    mergedProps.autoplay.enabled,
  );
  const [autoplayInterval, setAutoplayInterval] = useState<
    ReturnType<typeof setInterval> | number
  >(0);
  const [windowSize, setWindowSize] = useState(0);
  const [groupedData, setGroupedData] = useState<SlideProps[][]>([]);
  const [mainClasses, setMainClasses] = useState<string[]>([]);

  /**
   * Change active slide when customCurrentIndex is updated
   */
  useEffect(() => {
    carouselDispatch({
      type: 'goto',
      payload: { newSlideIndex: customCurrentIndex, source: 'prop' },
    });
  }, [customCurrentIndex]);

  /**
   * Class management
   */
  useEffect(() => {
    const newMainClasses = [
      mergedProps.classes?.component
        ? mergedProps.classes.component
        : 'carousel',
    ];
    if (mergedProps.classes?.custom) {
      newMainClasses.push(mergedProps.classes.custom);
    }
    setMainClasses(newMainClasses);
  }, [mergedProps]);

  const carouselClasses = useBemBlockClassNames(mainClasses);
  const carouselClassesTransitionModifier = useBemModifierClassNames(
    mainClasses,
    `is-${mergedProps.transition.type}-slide`,
  );
  const carouselClassesSlideListWrapperElement = useBemElementClassNames(
    mainClasses,
    `slide-list-wrapper`,
  );
  const carouselClassesSlideListElement = useBemElementClassNames(
    mainClasses,
    `slide-list`,
  );
  const carouselClassesSlideListElementIsChangingModifier =
    useBemModifierClassNames(
      carouselClassesSlideListElement,
      `is-changing-slide`,
    );
  const carouselClassesSlideElement = useBemElementClassNames(
    mainClasses,
    `slide`,
  );
  const carouselClassesSlideElementIsSelectedModifier =
    useBemModifierClassNames(carouselClassesSlideElement, `is-selected`);
  const carouselClassesSlideElementIsVisibleModifier = useBemModifierClassNames(
    carouselClassesSlideElement,
    `is-visible`,
  );
  const carouselClassesSlideInnerElement = useBemElementClassNames(
    mainClasses,
    `slide-inner`,
  );
  /**
   * End class management
   */

  /**
   * Merge settings management
   */
  const getCurrentBreakpointSettings = useCallback(():
    | CarouselProps
    | Record<string, never> => {
    let currentBreakpoint: SettingsBreakpoint | null = null;
    if (props.breakpointList?.length) {
      const currentWindowWidth = windowSize;
      props.breakpointList.forEach((breakpoint) => {
        if (
          breakpoint.size <= currentWindowWidth &&
          (!currentBreakpoint || currentBreakpoint.size <= breakpoint.size)
        ) {
          currentBreakpoint = breakpoint;
        }
      });
    }
    if (currentBreakpoint === null) {
      return {};
    }
    return currentBreakpoint['settings'] ?? {};
  }, [windowSize, props.breakpointList]);

  /**
   * mergeCarouselSettingsProperties will create a merged object between multiple object with the same properties.
   * It will look into every properties of the first object, and then override it by next objects
   * if the next object define the property.
   * In this case it will be the default properties,
   * override it by props if defined,
   * and then breakpoint if defined
   *
   * If the property is an object,
   * the function call herself to make the same for the object properties.
   * (So the params can either be the carousel settings or a part of it)
   */
  function mergeCarouselSettingsProperties(
    settingsProperties: Record<string, { constructor?: unknown }>[],
  ) {
    if (!settingsProperties.length) {
      return {};
    }
    const mergedSettings = { ...settingsProperties[0] };
    for (const propertyName in settingsProperties[0]) {
      if (
        settingsProperties[0][propertyName] !== null &&
        settingsProperties[0][propertyName].constructor === Object
      ) {
        const localSettingsProperties: Record<
          string,
          { constructor?: unknown }
        >[] = [];
        settingsProperties.forEach((setting) => {
          if (setting[propertyName]) {
            localSettingsProperties.push(
              setting[propertyName] as Record<
                string,
                { constructor?: unknown }
              >,
            );
          }
        });
        mergedSettings[propertyName] = mergeCarouselSettingsProperties(
          localSettingsProperties,
        );
      } else {
        let propertyFinalValue = settingsProperties[0][propertyName] ?? null;
        settingsProperties.forEach((setting) => {
          propertyFinalValue = setting[propertyName] ?? propertyFinalValue;
        });
        mergedSettings[propertyName] = propertyFinalValue;
      }
    }

    return mergedSettings;
  }

  /**
   * getMergedCarouselSettings is q function that will call mergeCarouselSettingsProperties
   * by passing him as params an array of 3 entity based on a common type (CarouselProps)
   * Entities are currently
   * - Default settings
   * - Props settings
   * - Current breakpoint's settings (if he exist)
   *
   * Since the default settings are of type RequiredCarouselBaseProps
   * and the final result will be an entity with RequiredCarouselBaseProps,
   * I'm typing it as one for IDE
   */
  // TODO: find a better way to manage dependancies for the  merged carousel settings
  const getMergedCarouselSettings =
    useCallback((): RequiredCarouselBaseProps => {
      const settings = [defaultSettings, props];

      const currentBreakpointSettings = getCurrentBreakpointSettings();
      if (
        currentBreakpointSettings &&
        Object.values(currentBreakpointSettings).length > 1
      ) {
        settings.push(currentBreakpointSettings);
      }
      return mergeCarouselSettingsProperties(
        settings as Record<string, { constructor?: unknown }>[],
      ) as unknown as RequiredCarouselBaseProps;
      // eslint-disable-next-line
    }, [
      getCurrentBreakpointSettings,
      props?.data,
      props?.renderComponentMethod,
      props?.isInfinite,
      props?.autoplay?.delay,
      props?.autoplay?.enabled,
      props?.autoplay?.pauseOnHover,
      props?.autoplay?.percentageVisibleForAutoplay,
      props?.classes?.component,
      props?.classes?.custom,
      props?.controls?.arrows?.classes?.component,
      props?.controls?.arrows?.classes?.custom,
      props?.controls?.arrows?.enabled,
      props?.controls?.arrows?.items?.classes?.component,
      props?.controls?.arrows?.items?.classes?.custom,
      props?.controls?.dots?.classes?.component,
      props?.controls?.dots?.classes?.custom,
      props?.controls?.dots?.enabled,
      props?.controls?.dots?.tint,
      props?.controls?.dots?.items?.classes?.component,
      props?.controls?.dots?.items?.classes?.custom,
      props?.controls?.swipe?.deltaForSwiping,
      props?.controls?.swipe?.enabled,
      props?.display?.itemByLine,
      props?.display?.lineBySlide,
      props?.display?.transition?.rotate?.numberOfItemsVisible,
      props?.display?.transition?.rotate?.translationPercentage,
      props?.events?.afterArrowClick,
      props?.events?.afterAutoplaySlideChange,
      props?.events?.afterDotClick,
      props?.events?.afterSlideChange,
      props?.events?.afterSwipe,
      props?.events?.onArrowClick,
      props?.events?.onAutoplaySlideChange,
      props?.events?.onDotClick,
      props?.events?.onSlideChange,
      props?.events?.onSwipe,
      props?.transition?.type,
      props?.transition?.easing,
      props?.transition?.time,
    ]);

  useEffect(() => {
    setMergedProps(getMergedCarouselSettings());
  }, [getMergedCarouselSettings]);

  useEffect(() => {
    setNumberOfItemBySlide(
      mergedProps.display.itemByLine * mergedProps.display.lineBySlide,
    );
  }, [mergedProps]);

  useEffect(() => {
    setNumberOfSlide(Math.ceil(mergedProps.data.length / numberOfItemBySlide));
  }, [mergedProps, numberOfItemBySlide]);

  useEffect(() => {
    carouselDispatch({
      type: 'reset',
      payload: {
        slideIndex: 0,
        maxIndex: numberOfSlide - 1,
      },
    });
  }, [numberOfSlide]);
  /**
   * End merge settings management
   */

  /**
   * Resize management
   */
  const handleWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);
  /**
   * End resize management
   */

  /**
   * Slide Management
   */
  useEffect(() => {
    setIsChangingSlide(true);

    if (mergedProps.events.onSlideChange) {
      mergedProps.events.onSlideChange(
        carouselState.currentSlideIndex,
        carouselState.latestSource,
      );
    }
    switch (carouselState.latestSource) {
      case 'arrow':
        if (mergedProps.events.onArrowClick && carouselState.direction) {
          mergedProps.events.onArrowClick(
            carouselState.currentSlideIndex,
            carouselState.direction as slideDirection,
          );
        }
        break;
      case 'dot':
        if (mergedProps.events.onDotClick) {
          mergedProps.events.onDotClick(carouselState.currentSlideIndex);
        }
        break;
      case 'swipe':
        if (mergedProps.events.onSwipe) {
          mergedProps.events.onSwipe(carouselState.currentSlideIndex);
        }
        break;
      default:
        break;
    }

    setTimeout(() => {
      setIsChangingSlide(false);

      if (mergedProps.events.afterSlideChange) {
        mergedProps.events.afterSlideChange(
          carouselState.currentSlideIndex,
          carouselState.latestSource,
        );
      }

      switch (carouselState.latestSource) {
        case 'arrow':
          if (mergedProps.events.afterArrowClick && carouselState.direction) {
            mergedProps.events.afterArrowClick(
              carouselState.currentSlideIndex,
              carouselState.direction as slideDirection,
            );
          }
          break;
        case 'dot':
          if (mergedProps.events.afterDotClick) {
            mergedProps.events.afterDotClick(carouselState.currentSlideIndex);
          }
          break;
        case 'swipe':
          if (mergedProps.events.afterSwipe) {
            mergedProps.events.afterSwipe(carouselState.currentSlideIndex);
          }
          break;
        default:
          break;
      }
    }, mergedProps.transition.time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselState.currentSlideIndex]);
  /**
   * End slide Management
   */

  /**
   * Autoplay management
   */
  const isVisible = useElementOnScreen(carouselRef, {
    root: null,
    rootMargin: '0px',
    threshold: mergedProps.autoplay.percentageVisibleForAutoplay / 100,
  });

  useEffect(() => {
    setAutoplayStatus(mergedProps.autoplay.enabled && isVisible);
  }, [isVisible, mergedProps.autoplay.enabled]);

  function handleCarouselMouseEnter() {
    setAutoplayStatus(false);
    autoplayCancel();
  }

  function handleCarouselMouseLeaving() {
    setAutoplayStatus(isVisible);
  }

  function autoplayInit() {
    const onAutoplayChangingSlide = () => {
      carouselDispatch({ type: 'next', payload: { source: 'autoplay' } });
    };

    const getAutoplayInterval = () => {
      return setInterval(() => {
        onAutoplayChangingSlide();
      }, mergedProps.autoplay.delay);
    };

    setAutoplayInterval(getAutoplayInterval());
  }

  /**
   * Force type for autoplayInterval in order to avoid IDE errors
   */
  function autoplayCancel() {
    if (autoplayInterval) {
      clearInterval(autoplayInterval as ReturnType<typeof setInterval>);
      setAutoplayInterval(0);
    }
  }

  useEffect(() => {
    if (autoplayStatus && isVisible) {
      autoplayInit();
    } else {
      autoplayCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayStatus, isVisible]);

  useEffect(() => {
    // clean when unmounting component
    return () => {
      autoplayCancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * End autoplay management
   */

  /**
   * Swipe management
   */
  function handleSwipeStart(userSwipeStartPosition: number) {
    setSwipeStart(userSwipeStartPosition);
    setIsSwiping(true);
  }

  function handleSwipeMove(userSwipePosition: number) {
    if (isSwiping) {
      setSwipeEnd(userSwipePosition);
    }
  }

  function handleSwipeEnd() {
    if (isSwiping) {
      if (swipeStart - swipeEnd > mergedProps.controls.swipe.deltaForSwiping) {
        carouselDispatch({ type: 'next', payload: { source: 'swipe' } });
      } else if (
        swipeStart - swipeEnd <
        -mergedProps.controls.swipe.deltaForSwiping
      ) {
        carouselDispatch({ type: 'prev', payload: { source: 'swipe' } });
      }

      setIsSwiping(false);

      return true;
    }
  }

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    handleSwipeStart(event.targetTouches[0].clientX);
  }

  function handleTouchMove(event: TouchEvent<HTMLDivElement>) {
    handleSwipeMove(event.targetTouches[0].clientX);
  }

  function handleTouchEnd() {
    handleSwipeEnd();
  }

  function handleMouseDown(event: MouseEvent<HTMLDivElement>) {
    handleSwipeStart(event.clientX);
  }

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    handleSwipeMove(event.clientX);
  }

  function handleMouseUp() {
    handleSwipeEnd();
  }

  function handleMouseLeave() {
    handleSwipeEnd();
  }

  const eventListToBindWhenCarouselPauseOnHover = {
    onMouseEnter: handleCarouselMouseEnter,
    onMouseLeave: handleCarouselMouseLeaving,
  };

  const eventListToBindWhenCarouselCanSwipe = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
  };
  /**
   * End Swipe management
   */
  /**
   * Group data for Multi item in view management
   */
  const getGroupedData = useCallback(() => {
    return mergedProps.data.reduce(
      (slideArray: SlideProps[][], slideItem, index) => {
        if (index % numberOfItemBySlide === 0) {
          slideArray.push([slideItem]);
        } else {
          slideArray[slideArray.length - 1].push(slideItem);
        }
        return slideArray;
      },
      [],
    );
  }, [mergedProps, numberOfItemBySlide]);

  useEffect(() => {
    setGroupedData(getGroupedData());
  }, [getGroupedData]);
  /**
   * End Group data for Multi item in view management
   */

  /**
   * Element custom key to manage infinite slider in rotate transition
   */
  const getSlideKeyDatasetWhenSliderIsInfinite = (
    selectedSlideIndex: number,
    currentSlideIndex: number,
    numberOfSlide: number,
  ) => {
    if (currentSlideIndex - selectedSlideIndex > 0) {
      if (
        currentSlideIndex - selectedSlideIndex >=
        Math.floor(numberOfSlide / 2)
      ) {
        /**
         * If the element is after the selected one and if further away than half the slider's slides length
         * return a negative value by getting the element index and subtracting the difference between the slider's slides length and the selected index
         */
        return currentSlideIndex - numberOfSlide - selectedSlideIndex;
      }
    } else {
      if (
        currentSlideIndex - selectedSlideIndex <=
        -1 * Math.floor(numberOfSlide / 2)
      ) {
        /**
         * If the element is before the selected one and if further away than half the slider's slides length
         * return a negative value by getting the element index and adding the difference between the slider's slides length and adding the selected index
         */
        return currentSlideIndex + numberOfSlide - selectedSlideIndex;
      }
    }
    return currentSlideIndex - selectedSlideIndex;
  };

  const checkIfSlideIsVisibleInRotateTransition = (
    selectedSlideIndex: number,
    currentSlideIndex: number,
    numberOfItemsVisible: number,
    numberOfSlide: number,
  ) => {
    if (mergedProps.transition.type !== 'rotate') {
      return false;
    }
    const defaultCheck =
      selectedSlideIndex >=
        currentSlideIndex - Math.floor(numberOfItemsVisible / 2) &&
      selectedSlideIndex <=
        currentSlideIndex + Math.floor(numberOfItemsVisible / 2);
    if (mergedProps.isInfinite) {
      /**
       * If you have less element than half the number of slides visible before the selected slide
       * return true if the element is at the end of the slider and should be display
       */
      const infiniteCheckBefore =
        selectedSlideIndex < Math.floor(numberOfItemsVisible / 2) &&
        numberOfSlide - currentSlideIndex - selectedSlideIndex <=
          Math.floor(numberOfItemsVisible / 2);
      /**
       * If you have less element than half the number of slides visible after the selected slide
       * return true if the element is at the start of the slider and should be display
       */
      const infiniteCheckAfter =
        selectedSlideIndex >=
          numberOfSlide - Math.floor(numberOfItemsVisible / 2) &&
        numberOfSlide - selectedSlideIndex + currentSlideIndex <=
          Math.floor(numberOfItemsVisible / 2);
      return defaultCheck || infiniteCheckBefore || infiniteCheckAfter;
    }
    return defaultCheck;
  };

  if (!mergedProps.data.length) {
    return null;
  }

  return (
    <StyledCarousel
      currentSlideIndex={carouselState.currentSlideIndex}
      transition={mergedProps.transition}
      dotsSettings={mergedProps.controls.dots}
      arrowsSettings={mergedProps.controls.arrows}
      carouselDisplay={mergedProps.display}
      className={`${carouselClasses} ${carouselClassesTransitionModifier}`}
      aria-label="carousel"
      {...(mergedProps.autoplay.enabled &&
        mergedProps.autoplay.pauseOnHover &&
        eventListToBindWhenCarouselPauseOnHover)}
    >
      <p className="sr-only">
        Carousel with three slides shown at a time. Use the Previous and Next
        buttons to navigate, or the slide dot buttons at the end to jump to
        slides.
      </p>
      {mergedProps.slideList.wrapper({
        children: (
          <div
            className={`${carouselClassesSlideListWrapperElement}`}
            ref={carouselRef}
            {...(mergedProps.controls?.swipe?.enabled &&
              eventListToBindWhenCarouselCanSwipe)}
          >
            <ul
              className={`${carouselClassesSlideListElement} ${
                isChangingSlide
                  ? carouselClassesSlideListElementIsChangingModifier
                  : ''
              }`}
            >
              {groupedData.map((groupedSlide, mapIndex) => (
                <StyledCarouselSlideElement
                  className={`${carouselClassesSlideElement} ${
                    carouselState.currentSlideIndex === mapIndex
                      ? `${carouselClassesSlideElementIsSelectedModifier}`
                      : ''
                  } ${
                    checkIfSlideIsVisibleInRotateTransition(
                      carouselState.currentSlideIndex,
                      mapIndex,
                      mergedProps.display.transition.rotate
                        .numberOfItemsVisible,
                      groupedData.length,
                    )
                      ? `${carouselClassesSlideElementIsVisibleModifier}`
                      : ''
                  }`}
                  key={mapIndex}
                  keyDataset={
                    mergedProps.isInfinite
                      ? getSlideKeyDatasetWhenSliderIsInfinite(
                          carouselState.currentSlideIndex,
                          mapIndex,
                          groupedData.length,
                        )
                      : mapIndex - carouselState.currentSlideIndex
                  }
                  numberOfSlide={groupedData.length}
                  translationPercentage={
                    mergedProps.display.transition.rotate.translationPercentage
                  }
                  transition={mergedProps.transition}
                  role="group"
                  aria-label={`slide ${mapIndex + 1} of ${numberOfSlide}`}
                >
                  {groupedSlide.map((slide: SlideProps) => (
                    <div
                      key={slide.id}
                      className={`${carouselClassesSlideInnerElement}`}
                    >
                      {mergedProps.renderComponentMethod({ item: slide })}
                    </div>
                  ))}
                </StyledCarouselSlideElement>
              ))}
            </ul>
          </div>
        ),
      })}
      {(mergedProps.controls.arrows.enabled ||
        mergedProps.controls.dots.enabled) &&
        mergedProps.controls.wrapper({
          children: (
            <>
              {mergedProps.controls.arrows.enabled && (
                <Arrows
                  isInfinite={mergedProps.isInfinite}
                  carouselState={carouselState}
                  settings={mergedProps.controls.arrows}
                  carouselDispatch={carouselDispatch}
                />
              )}

              {mergedProps.controls.dots.enabled && (
                <Dots
                  numberOfDots={numberOfSlide}
                  currentSlideIndex={carouselState.currentSlideIndex}
                  settings={mergedProps.controls.dots}
                  carouselDispatch={carouselDispatch}
                />
              )}
            </>
          ),
        })}
    </StyledCarousel>
  );
};
Carousel.displayName = 'Carousel';

export default Carousel;
